/**
 * Picks up a new deploy when the app returns to the foreground.
 *
 * An open client only learns about a deploy through one of three triggers: a full page load (the
 * navigation route is NetworkFirst, so index.html is always fresh and `registerType: 'autoUpdate'`
 * installs the new service worker), a chunk that a new deploy removed (`emitRouteChunkError:
 * 'automatic-immediate'`), or Nuxt's own check-outdated-build plugin polling
 * `builds/latest.json`. During an event most attendees keep the PWA open all day and only move
 * between routes they have already loaded, so none of the first two fire and the poll interval is
 * the whole story — a hotfix reaches them only after it elapses.
 *
 * Returning to the foreground is both the moment a stale client is most likely (the app sat
 * backgrounded across the deploy) and the moment a reload costs the least, so that is when this
 * checks. It is deliberately the same comparison and the same reload Nuxt performs on its timer,
 * not a second mechanism: `latest.json`'s build id against the one this bundle was built with.
 */
import { joinURL } from 'ufo';

// Minimum gap between checks. `latest.json?<ts>` is a Cloudflare MISS by construction — the query
// is what stops Pages serving a month-old `/_nuxt` edge copy — so every check reaches the origin,
// and a phone going pocket-to-hand must not be able to turn that into a stream of requests.
const CHECK_THROTTLE_MS = 60_000;

// Build id this tab last reloaded for, and when another attempt at it is allowed again.
//
// `reloadNuxtApp` has its own loop guard, but it expires after 10s, which is not enough here: a
// reload can land back on the *same* build if the shell comes from the service worker's cache
// instead of the network (the navigation route is NetworkFirst with a 3s timeout, and event WiFi
// hits that). Without this the app would reload on every single foreground until the network
// recovers.
//
// The window is deliberately short rather than a once-per-build latch. A reload that failed to
// pick the new build up has to be retried — that is the whole point of the feature, and leaving it
// to the 10-minute fallback timer (itself throttled while backgrounded) is what this replaces. So
// a failed attempt only suppresses the next couple of minutes of retries; a successful one never
// comes back here at all, because the build id then matches.
const RELOAD_ATTEMPT_KEY = 'wad:outdated-build-reload';
const RELOAD_RETRY_AFTER_MS = 2 * 60_000;

// sessionStorage throws in some privacy modes and can hold anything a previous version wrote, so
// neither side of this is allowed to break the check: an unreadable attempt means "not attempted"
// and falls back to reloadNuxtApp's own guard.
const hasRecentReloadAttempt = (buildId: string): boolean => {
  try {
    const attempt = JSON.parse(sessionStorage.getItem(RELOAD_ATTEMPT_KEY) || '{}');
    return attempt?.id === buildId && attempt?.expires > Date.now();
  } catch {
    return false;
  }
};

const rememberReloadAttempt = (buildId: string) => {
  try {
    sessionStorage.setItem(
      RELOAD_ATTEMPT_KEY,
      JSON.stringify({ id: buildId, expires: Date.now() + RELOAD_RETRY_AFTER_MS }),
    );
  } catch {}
};

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const currentRoute = useRouter().currentRoute;
  const { isOnline } = useConnectivity();

  // Same URL Nuxt's check-outdated-build plugin polls, built the way `buildAssetsURL` builds it so
  // a cdnURL (or a non-root baseURL) keeps working.
  const manifestUrl = joinURL(
    config.app.cdnURL || config.app.baseURL,
    config.app.buildAssetsDir,
    'builds/latest.json',
  );

  let lastCheckedAt = 0;
  let checking = false;

  const checkForNewBuild = async () => {
    // Both triggers below can fire inside the same resume — a phone coming out of a pocket on a
    // recovering connection raises visibilitychange and flips isOnline — so they can race.
    if (checking) return;

    // The app's connectivity state, not navigator.onLine — see useConnectivity for why that flag
    // is not trusted anywhere in this app. Offline, the check can only fail and a reload would
    // land on the cached shell, so there is nothing to gain by trying.
    if (!isOnline.value) return;
    if (Date.now() - lastCheckedAt < CHECK_THROTTLE_MS) return;

    // Stamped on the attempt rather than on success: a failing request costs the origin the same
    // as a successful one, so it has to count against the throttle too.
    lastCheckedAt = Date.now();
    checking = true;

    try {
      const latest = await $fetch<{ id?: string }>(`${manifestUrl}?${Date.now()}`, {
        responseType: 'json',
      });

      // No buildId to compare against means we cannot tell new from old, and reloading on that
      // would loop. Anything other than a clean id mismatch is treated as "nothing to do".
      if (!config.app.buildId || !latest?.id || latest.id === config.app.buildId) return;

      // A reload for this build id was already tried and did not take; give it a moment before
      // trying again rather than reloading on every foreground.
      if (hasRecentReloadAttempt(latest.id)) return;
      rememberReloadAttempt(latest.id);

      // Nuxt's own reload path, so this behaves exactly like the reload its timer would have done.
      reloadNuxtApp({
        path: joinURL(config.app.baseURL, currentRoute.value.fullPath),
        persistState: true,
      });
    } catch {
      // A check that fails is not actionable — the app keeps running on the build it has and the
      // next foreground tries again — so it stays silent rather than surfacing in Bugsnag.
    } finally {
      checking = false;
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    checkForNewBuild();
  });

  // A foreground arrival with no connection skipped its check; run it once the connection is back,
  // but only while the app is actually in front of someone.
  watch(isOnline, (online) => {
    if (online && document.visibilityState === 'visible') {
      checkForNewBuild();
    }
  });
});
